import State from "../lib/State";

import ServiceLocator from "../ServiceLocator";
import * as PIXI from "pixi.js";
import GameplayScreen from "../screen/GameplayScreen";
import { IGameSnacks } from "../lib/GameSnacks";
import AdManager from "../lib/AdManager";
import Screen from "../lib/Screen";
import LobbyScreen from "../screen/LobbyScreen";
import TweenGroup from "../lib/TweenGroup";
import MultiplayerEventManager, { game_event_type } from "../service/MultiplayerEventManager";
import { IInit, Profile } from "../lib/game_snacks/IUser";
import Session, { game_type } from "../Session";
import { LiveEventData, LiveEventDataType, LivePlayerDataEvent, LivePresenceAction, LiveRoomData } from "../lib/game_snacks/ILiveMultiplayer";
import Utils from "../Utils";
import PrivateRoomPopup from "../screen/popup/HostPrivateRoomPopup";
import GuestPrivateRoomPopup from "../screen/popup/GuestPrivateRoomPopup";
import MessagePopup from "../screen/popup/MessagePopup";
import FakeMatchMakingScreen from "../screen/FakeMatchMakingScreen";
import ShopScreen from "../screen/ShopScreen";
import { GameSession } from "../lib/game_snacks/IMultiplayer";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class GameState extends State
{
    // #region Attributes //

    // private:

    private _curScreen: Screen = null; 

    private _arrInvitationEvent: Array<LiveEventData> = null;
    
    private _adManager: AdManager = null;
    private _tweenGroup: TweenGroup = null;

    private _waitingPopup: MessagePopup = null;
    private _checkPrivateRoomTimer: number = null;

    private _state: game_state_type = null;

    private _isReady: boolean = null;

    // #endregion //


    public get ad(): AdManager { return this._adManager; }
    public get tweenGroup(): TweenGroup { return this._tweenGroup; }


    // #region Methods //

    // public:

    public constructor() 
    { 
        super(); 
    }

    public onEnter( params: Object = null ): void
    {
        this._state = game_state_type.IDLE;
        this._isReady = false;

        this._adManager = new AdManager();
        this._adManager.init();

        this._tweenGroup = new TweenGroup();
        this._tweenGroup.init();

        ServiceLocator.multiplayerEventManager = new MultiplayerEventManager();
        ServiceLocator.multiplayerEventManager.onProfileChange.addOnce( ( profile: Profile ) => {
            Session.i.profile = profile;

            if ( Session.i.profile == null && Session.i.isInvite )
            {
                this._state = game_state_type.IDLE;

                ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
            
                ServiceLocator.app.root.interactiveChildren = true;
            }
        } );
            
        this._initMultiplayerAPI();

        // TEST:
        /*let g = new PIXI.Graphics();
        g.beginFill( 0xffffff );
        g.drawRect( 0, 0, ServiceLocator.app.width, ServiceLocator.app.height );
        g.endFill();
        ServiceLocator.app.stage.addChild( g );

        for ( let i = 0; i < 3; ++i )
        {
            let test = new PIXI.Sprite( ServiceLocator.resources[ [ "test1x", "test2x", "test4x" ][ i ] ].texture );
            test.position.set( 100, 100 + i * 150 );
            ServiceLocator.app.stage.addChild( test );
        }*/

        ServiceLocator.audio.playMusic( "track_0", { loop: true } );
    }
        
        private _initMultiplayerAPI(): void
        {
            // IMPORTANT: For private game. Cannot be done inside the GameSnacks.init() callback, since presence update and room joined events are received before GameSnacks.init() callback is invoked. 
            this._arrInvitationEvent = [];
            ServiceLocator.multiplayerEventManager.onRoomLoaded.add( this._onRoom_Loaded, this );
            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );
            ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
            //

            ServiceLocator.app.root.interactiveChildren = false;
            //
            window.GameSnacks.init()
                .then( ( value: IInit ) => {
                    // DEBUG:
                    Session.i.isMessages = value.isMessages;
                    Session.i.isInvite = value.isInvite;
                    /*Session.i.isMessages = true;
                    Session.i.isInvite = true;*/

                    // Show waiting popup.
                    if ( Session.i.isInvite )
                    {
                        if ( Session.i.isMessages )
                        {
                            this._waitingPopup = Utils.showMessagePopup( "Loading game...", false );
                        }
                        else
                        {
                            this._waitingPopup = Utils.showMessagePopup( "Connecting to private room...", false );
                        }
                    }
                    else
                    {
                        if ( Session.i.isMessages )
                        {
                            this._waitingPopup = Utils.showMessagePopup( "Creating game...", false );
                        }
                    }

                    if ( Session.i.isInvite )
                    {
                        if ( Session.i.isMessages )
                        {
                            this._joinAsyncGame();
                        }
                        else
                        {
                            // Private game invitation.
                            if ( this._state == game_state_type.IDLE )
                            {
                                this._checkPrivateRoomTimer = 0;
                                        
                                this._state = game_state_type.CHECK_PRIVATE_ROOM;
                            }
                        }
                    }
                    else
                    {
                        // Remove private game callbacks.
                        ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                        ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();

                        if ( Session.i.isMessages )
                        {
                            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.addOnce( this._onMultiplayer_PlayerPresenceUpdated, this );
            
                            this._createAsyncGame();
                        }
                    }

                    // Screen selection.
                    if ( value.isInvite || value.isMessages )
                    {
                        ServiceLocator.saved.data.isFirstGame = false;
                        ServiceLocator.saved.save();
                    }

                    if ( ServiceLocator.saved.data.isFirstGame )
                    {
                        if ( !Session.i.profile )
                        {
                            Session.i.profile = { 
                                username: { prefix: "You", suffix: "" },
                                avatar: { src: "avatar_you", alt: null },
                                uid: "fake",
                                isFake: true };
                        }

                        let fakeMatchmakingScreen: FakeMatchMakingScreen = new FakeMatchMakingScreen();
                        fakeMatchmakingScreen.onEnter();
                        fakeMatchmakingScreen.onCompleted.addOnce( this._onScreen_Completed, this );
                        this._curScreen = fakeMatchmakingScreen;
                        
                        ServiceLocator.app.root.interactiveChildren = true;
                    }
                    else 
                    {
                        if ( !value.isMessages )
                        {
                            if ( value.isInvite )
                            {
                                // Private game.
                                this._curScreen = new LobbyScreen();
                                this._curScreen.onEnter();
                                this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );
                            }
                            else
                            {
                                // Regular game start (no private game, no messages).
                                ServiceLocator.app.root.interactiveChildren = true;

                                this._curScreen = new LobbyScreen();
                                this._curScreen.onEnter();
                                this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );
                            }
                        }
                    }

                    if ( !this._isReady )
                    {
                        this._isReady = true;

                        window.GameSnacks.game.ready();
                    }
                } );
        }

            private _joinAsyncGame(): void
            {
                this._state = game_state_type.CHECK_ASYNC_GAME;

                // Remove private game callbacks.
                ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();

                ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.addOnce( this._onMultiplayer_PlayerPresenceUpdated, this );

                try
                {
                    // TEST:
                    window.GameSnacks.multiplayer.getGameState().then( ( gameSession: GameSession ) => {
                        
                        /*let gameSession: GameSession = {
                            conversationId: "conversation-id",
                            gameCenterId: "messages",
                            gameId: "7vtnrtrku7v1o",
                            gameState: "{\"arrPlayerProfile\":[{\"username\":{\"prefix\":\"jonam\",\"suffix\":\"7576\"},\"avatar\":{\"src\":\"https://gamesnacks.com/images/avatars/mystic_mushroom.svg\",\"alt\":\"Mystic Mushroom\"},\"dartSkinIndex\":0},{\"username\":{\"prefix\":\"gabby\",\"suffix\":\"4389\"},\"avatar\":{\"src\":\"https://gamesnacks.com/images/avatars/vintagon.svg\",\"alt\":\"Vintagon\"},\"uid\":\"2140a030-8b6f-4677-b8dd-31a7e8de3351\",\"dartSkinIndex\":0}],\"arrPlayerEventIndex\":[2,2],\"arrPlayerEvent\":[{\"payload\":\"{\\\"type\\\":0,\\\"playerId\\\":0,\\\"target\\\":[1.2237133979797363,4.356916904449463,-20]}\"},{\"payload\":\"{\\\"type\\\":0,\\\"playerId\\\":0,\\\"target\\\":[2.4614481925964355,4.876624584197998,-20]}\"}]}",
                            gameVersionId: "0.0.73",
                            id: "b20b33e5-8fb2-4746-98c8-0294e8ee3a11",
                            recipientPhoneNumber: "07700 900 901",
                            senderPhoneNumber: "07700 900 900"
                        };*/

                        this._state = game_state_type.IDLE;
                        ServiceLocator.app.root.interactiveChildren = true;

                        if ( !Session.i.profile )
                        {
                            Session.i.profile = { 
                                username: { prefix: "You", suffix: "" },
                                avatar: { src: "avatar_you", alt: null },
                                uid: "fake",
                                dartSkinIndex: 0,
                                isFake: true };
                        }
                        else
                        {
                            Session.i.profile.dartSkinIndex = ServiceLocator.saved.data.selDartIndex;
                        }

                        if ( gameSession.gameState )
                        {
                            Session.i.asyncGameState = JSON.parse( gameSession.gameState );
                            if ( Session.i.asyncGameState.arrPlayerProfile[ 0 ].uid == Session.i.profile.uid )
                            {
                                Session.i.asyncGameState.arrPlayerProfile[ 0 ] = Session.i.profile;
                            }
                            else
                            {
                                Session.i.asyncGameState.arrPlayerProfile[ 1 ] = Session.i.profile;
                            }
                        }
                        else
                        {
                            Session.i.asyncGameState = {
                                playerOwnerId: 0,
                                arrPlayerProfile: [ null, Session.i.profile ],
                                arrPlayerEventIndex: [ 0, 0 ],
                                arrPlayerEvent: [],
                                arrIsRematchRequested: [ false, false ]
                            };
                        }

                        if ( !Session.i.asyncGameState.arrPlayerProfile[ 0 ] )
                        {
                            Session.i.asyncGameState.arrPlayerProfile[ 0 ] = { 
                                username: { prefix: "Them", suffix: "" },
                                avatar: { src: "avatar_you", alt: null },
                                uid: "fake",
                                dartSkinIndex: 0,
                                isFake: true 
                            };
                        }

                        Session.i.room = Session.i.asyncGameState.arrPlayerProfile;

                        Session.i.gameType = game_type.CLASSIC_ASYNC;

                        this._waitingPopup.onLeave();
                        this._waitingPopup = null;

                        this._curScreen = new GameplayScreen();
                        this._curScreen.onEnter();
                        this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );  
                    } );
                }
                catch( err )
                {
                    ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();

                    this._waitingPopup.onLeave();
                    this._waitingPopup = null;

                    let messagePopup: MessagePopup = Utils.showMessagePopup( "The game is no longer available." );
                    messagePopup.onClosed.addOnce( () => {
                        messagePopup.onLeave();

                        Session.i.isMessages = false;
                        Session.i.isInvite = false;

                        this._state = game_state_type.IDLE;
                        ServiceLocator.app.root.interactiveChildren = true;

                        let lobbyScreen: LobbyScreen = new LobbyScreen();
                        lobbyScreen.onEnter();
                        lobbyScreen.onCompleted.addOnce( this._onScreen_Completed, this );
                        this._curScreen = lobbyScreen;
                    } );
                }
            }

            private  _createAsyncGame(): void
            {
                window.GameSnacks.liveMultiplayer.createRoom().then( 
                    ( privateRoomUrl: string ) => { 
                        console.log( "async game invite url=" + privateRoomUrl );

                        if ( !Session.i.profile )
                        {
                            Session.i.profile = { 
                                username: { prefix: "You", suffix: "" },
                                avatar: { src: "avatar_you", alt: null },
                                uid: "fake",
                                dartSkinIndex: 0,
                                isFake: true,
                            };
                        }
                        //
                        Session.i.profile.dartSkinIndex = ServiceLocator.saved.data.selDartIndex;

                        Session.i.asyncGameState = {
                            playerOwnerId: 0,
                            arrPlayerProfile: [ 
                                Session.i.profile,
                                { 
                                    username: { prefix: "Them", suffix: "" },
                                    avatar: { src: "avatar_you", alt: null },
                                    uid: "fake",
                                    dartSkinIndex: 0,
                                    isFake: true 
                                }
                            ],
                            arrPlayerEventIndex: [ 0, 0 ],
                            arrPlayerEvent: [],
                            arrIsRematchRequested: [ false, false ]
                        };
                        try
                        {
                            window.GameSnacks.multiplayer.updateGameState( JSON.stringify( Session.i.asyncGameState ) ).then( () => {
                                Session.i.room = Session.i.asyncGameState.arrPlayerProfile;

                                this._state = game_state_type.IDLE;
                                ServiceLocator.app.root.interactiveChildren = true;

                                Session.i.gameType = game_type.CLASSIC_ASYNC;

                                this._waitingPopup.onLeave();
                                this._waitingPopup = null;

                                this._curScreen = new GameplayScreen();
                                this._curScreen.onEnter();
                                this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );
                            } );
                        }
                        catch ( err )
                        {
                            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                            
                            this._waitingPopup.onLeave();
                            this._waitingPopup = null;

                            this._curScreen = new LobbyScreen();
                            this._curScreen.onEnter();
                            this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );

                            Session.i.isMessages = false;
                            Session.i.isInvite = false;

                            this._state = game_state_type.IDLE;
                            ServiceLocator.app.root.interactiveChildren = true;

                            Utils.showMessagePopup( "The game cannot be created." );
                        }
                    }
                );
            }

            private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
            {
                presenceEventPromise
                    .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                        if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                        {
                            if ( livePlayerDataEvent.isLocalPlayer )
                            {
                                let profile: Profile = livePlayerDataEvent.profile;
                                profile.uid = livePlayerDataEvent.uid;

                                if ( !Session.i.profile )
                                {
                                    Session.i.profile = profile;
                                }
                                else
                                {
                                    Session.i.profile.uid = profile.uid;
                                }

                                if ( Session.i.isMessages )
                                {
                                    // TODO:
                                }
                                else
                                {
                                    // Only for private games.
                                    this._checkPrivateRoomTimer = 0;

                                    this._state = game_state_type.CHECK_INVITATION_EVENTS;
                                }
                            }
                        }
                    } );
            }

            private _onRoom_Loaded( liveRoomData: Promise<LiveRoomData> ): void
            {
                liveRoomData
                    .then( ( liveRoomData: LiveRoomData ) => {
                        Session.i.room = [];
                        for ( let i: number = 0; i < liveRoomData.players.length; ++i )
                        {
                            if ( liveRoomData.players[ i ].action == LivePresenceAction.JOIN )
                            {
                                let profile: Profile = liveRoomData.players[ i ].profile;
                                profile.uid = liveRoomData.players[ i ].uid;
                                Session.i.room.push( profile );
                            }
                        }
                        //console.log( "_onRoom_Loaded :: " + Session.i.room.length );
                    } );
            }

            private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
            {
                eventsPromise
                    .then( ( arrData: LiveEventData[] ) => {
                        this._arrInvitationEvent.push( ...arrData );
                    } );
            }

                private _onGuestPrivateRoomPopup_Closed( popup: GuestPrivateRoomPopup ): void
                {
                    popup.onLeave();

                    Session.i.room = [];
                }

                private _onGuestPrivateRoomPopup_GameStarted( popup: GuestPrivateRoomPopup, gameType: game_type ): void
                {
                    popup.onLeave();

                    Session.i.gameType = gameType;

                    this._onScreen_Completed( screen_type.GAMEPLAY );
                }

        private _onScreen_Completed( nextScreen: screen_type, params: any = null ): void
        {
            this._curScreen.onLeave();

            if ( ServiceLocator.saved.data.isFirstGame && nextScreen == screen_type.LOBBY )
            {
                ServiceLocator.saved.data.isFirstGame = false;
                ServiceLocator.saved.save();

                if ( Session.i.profile.isFake )
                {
                    Session.i.profile = null;
                }
                Session.i.room = null;
            }
            
            switch ( nextScreen )
            {
                case screen_type.LOBBY: { this._curScreen = new LobbyScreen(); break; }
                case screen_type.GAMEPLAY: { this._curScreen = new GameplayScreen(); break; }
                case screen_type.FAKE_MATCH_MAKING: { this._curScreen = new FakeMatchMakingScreen(); break; }
                case screen_type.SHOP: { this._curScreen = new ShopScreen(); break; }
            }
            this._curScreen.onEnter( params );
            this._curScreen.onCompleted.addOnce( this._onScreen_Completed, this );
        }

    public onLeave(): void
    {
        this._curScreen.onLeave();
        this._curScreen = null;

        this._adManager.end();
        this._adManager = null;

        this._tweenGroup.end();
        this._tweenGroup = null;
    
        super.onLeave();
    }

    public onUpdate( dt: number ): void
    {
        if ( this._curScreen )
        {
            this._curScreen.onUpdate( dt );
        }

        if ( this._state == game_state_type.CHECK_PRIVATE_ROOM )
        {
            this._checkPrivateRoomTimer += dt;
            if ( this._checkPrivateRoomTimer >= 6 )
            {
                this._waitingPopup.onLeave();
                this._waitingPopup = null;

                Session.i.room = []
                Session.i.isInvite = false;

                ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
            
                this._state = game_state_type.IDLE;
                ServiceLocator.app.root.interactiveChildren = true;

                Utils.showMessagePopup( "The room no longer exists." );
            }
        }
        else if ( this._state == game_state_type.CHECK_INVITATION_EVENTS )
        {
            if ( Session.i.room.length > 0 )
            {
                while ( this._arrInvitationEvent.length > 0 )
                {
                    let data: LiveEventData = this._arrInvitationEvent.shift();
                    if ( !data.type )
                    {
                        const kPayload: any = JSON.parse( data.payload );
                        if ( ( kPayload.type == game_event_type.PRIVATE_ROOM_FULL
                                || kPayload.type == game_event_type.GAME_ALREADY_STARTED )
                            && kPayload.uid == Session.i.profile.uid )
                        {
                            this._waitingPopup.onLeave();
                            this._waitingPopup = null;

                            window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE );
                        
                            Session.i.room = []
                            Session.i.isInvite = false;
                            Session.i.profile.uid = null;

                            ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                            ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();

                            this._arrInvitationEvent = [];

                            this._state = game_state_type.IDLE;
                            ServiceLocator.app.root.interactiveChildren = true;

                            if ( kPayload.type == game_event_type.PRIVATE_ROOM_FULL )
                            {
                                Utils.showMessagePopup( "Room full." );
                            }
                            else
                            {
                                Utils.showMessagePopup( "The game is no longer available." );
                            }
                        }
                        else if ( kPayload.type == game_event_type.PRIVATE_ROOM_ACCESS_GRANTED ) 
                        {
                            if ( Session.i.profile.uid == kPayload.uid )
                            {
                                this._waitingPopup.onLeave();
                                this._waitingPopup = null;

                                Session.i.isInvite = false;

                                ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                                ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                                ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
                            
                                //console.log( "_onMultiplayer_EventsReceived :: " + Session.i.room.length );

                                let privateRoomPopup: GuestPrivateRoomPopup = new GuestPrivateRoomPopup();
                                privateRoomPopup.gameType = kPayload.gameType;
                                privateRoomPopup.onEnter();
                                privateRoomPopup.onClosed.add( this._onGuestPrivateRoomPopup_Closed, this );
                                privateRoomPopup.onGameStarted.add( this._onGuestPrivateRoomPopup_GameStarted, this );

                                this._arrInvitationEvent = [];

                                this._state = game_state_type.IDLE;
                                ServiceLocator.app.root.interactiveChildren = true;
                            }
                        }
                    }
                }
            }

            if ( this._state == game_state_type.CHECK_INVITATION_EVENTS )
            {
                this._checkPrivateRoomTimer += dt;
                if ( this._checkPrivateRoomTimer >= 6 )
                {
                    this._waitingPopup.onLeave();
                    this._waitingPopup = null;

                    Session.i.room = []
                    Session.i.isInvite = false;

                    ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
                    ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
                    ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();

                    this._arrInvitationEvent = [];

                    this._state = game_state_type.IDLE;
                    ServiceLocator.app.root.interactiveChildren = true;

                    Utils.showMessagePopup( "The room no longer exists." );
                }
            }
        }
    }

    // #endregion //
}

export const enum screen_type
{
    LOBBY = 0,
    FAKE_MATCH_MAKING,
    GAMEPLAY,
    SHOP
}

const enum game_state_type
{
    IDLE = 0,
    CHECK_PRIVATE_ROOM,
    CHECK_INVITATION_EVENTS,
    CHECK_ASYNC_GAME,
    CREATE_ASYNC_GAME
}